import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  IPaymentsLog,
  IReduxRequestNode,
  IPaginator,
  IPaymentsLogItem,
} from "@litres-libs/types";
import { Request } from "../../../request/Request";
import { addHydrateReducer } from "../../../utils/hydrateReducer";

interface IPaymentsState {
  payments: IPaymentsLog;
  actions: {
    paymentsHistoryCSRF: IReduxRequestNode<
      IPaymentsGUJRequest,
      IPaymentsGUJResponse
    >;
  };
}

export interface IPaymentsGUJResponse {
  paginator: IPaginator;
  [page: number]: IPaymentsLogItem[];
}
interface IPaymentsGUJRequest {
  pagenum: number;
}

const initialState: IPaymentsState = {
  payments: {
    paginator: {
      page: 0,
      limit: 0,
      maxPageNumber: 0,
      items: 0,
    },
    pages: [],
  },
  actions: {
    paymentsHistoryCSRF: {},
  },
};

export const paymentsHistoryCSRF = createAsyncThunk(
  "pages/personalCabinet/paymentsHistoryCSFR",
  async (params: { page: number }) => {
    const response = new Request.CSRF<
      IPaymentsGUJRequest,
      IPaymentsGUJResponse
    >({
      url: "/pages/personal_cabinet_history_log/?gu_ajax=1&lite=1",
      provider: {
        id: "GET_PAYMENTS_HISTORY",
      },
      request: {
        method: "GET",
        params: {
          pagenum: params.page,
        },
      },
    });

    return await response.fetch();
  }
);

export const paymentsHistorySlice = createSlice({
  name: "pages/personalCabinet/paymentsHistory",
  initialState,
  reducers: {
    setHistoryData: (
      state: IPaymentsState,
      action: PayloadAction<IPaymentsLog>
    ) => {
      state.payments = action.payload;
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "pages.personalCabinet.paymentsHistory");
    /**
     * Обработка загрузки и ответа при получении данных об истории платежей
     */
    builder.addCase(paymentsHistoryCSRF.pending, (state: IPaymentsState) => {
      state.actions.paymentsHistoryCSRF.isLoading = true;
      state.actions.paymentsHistoryCSRF.error = undefined;
    });
    builder.addCase(
      paymentsHistoryCSRF.fulfilled,
      (
        state: IPaymentsState,
        action: PayloadAction<
          IReduxRequestNode<IPaymentsGUJRequest, IPaymentsGUJResponse>
        >
      ) => {
        state.actions.paymentsHistoryCSRF = action.payload;
        state.actions.paymentsHistoryCSRF.isLoading = false;

        // Страницы хранятся в массиве под своим номером (пришла страница 5 - ляжет в индекс 5 в массив)
        const requestedPageNumber =
          action.payload.request?.request?.params?.pagenum;
        if (requestedPageNumber && action.payload.response) {
          state.payments.pages[requestedPageNumber] =
            action.payload.response[requestedPageNumber];
          state.payments.paginator = action.payload.response.paginator;
        }

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          state.actions.paymentsHistoryCSRF.error = action.payload.error;
          return;
        }
      }
    );
  },
});

export const { setHistoryData } = paymentsHistorySlice.actions;

export default paymentsHistorySlice.reducer;
